import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Typography,
  Grid,
  TextField,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  IconButton,
  useTheme,
  alpha,
  styled
} from '@mui/material';
import {
  FileDownload as ExportIcon,
  Close as CloseIcon,
  DateRange as DateIcon,
  GetApp as DownloadIcon,
  CheckCircle as CheckIcon
} from '@mui/icons-material';
import apiService from 'Network/apiService';
import { API_ENDPOINTS } from 'Network/apiEndpoints';

const StyledCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  backgroundColor: alpha(theme.palette.background.paper, 0.8),
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  '& .MuiCardContent-root': {
    padding: theme.spacing(2),
    '&:last-child': {
      paddingBottom: theme.spacing(2)
    }
  }
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(2),
  padding: theme.spacing(1, 0),
  borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  '& .MuiSvgIcon-root': {
    color: theme.palette.primary.main,
    fontSize: '1.2rem'
  },
  '& .MuiTypography-root': {
    fontWeight: 600,
    color: theme.palette.primary.dark
  }
}));

const FieldVisitExportModal = ({ open, onClose, onRefresh }) => {
  const theme = useTheme();
  
  // State for filters
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Date range state
  const [dateRange, setDateRange] = useState({
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });
  const [dateFilter, setDateFilter] = useState('thisMonth');

  // Initialize date range when modal opens
  useEffect(() => {
    if (open) {
      handleDateFilterChange('thisMonth');
    }
  }, [open]);

  // Handle date filter changes
  const handleDateFilterChange = (filterType) => {
    setDateFilter(filterType);
    const today = new Date();
    
    const format = (date) => date.toISOString().split("T")[0];


   switch (filterType) {
    case "thisMonth": {
      const start = new Date(today.getFullYear(), today.getMonth() - 1, today.getDate());
      const end = today;
      setDateRange({
        startDate: format(start),
        endDate: format(end)
      });
      break;
    }

    case "custom": {
      const { startDate, endDate } = dateRange;

      if (!startDate || !endDate) {
        const start = new Date(today.getFullYear(), today.getMonth() - 1, today.getDate());
        const end = today;
        setDateRange({
          startDate: format(start),
          endDate: format(end)
        });
      }
      break;
    }

    case "lastMonth": {
      const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const end = new Date(today.getFullYear(), today.getMonth(), 0);
      setDateRange({
        startDate: format(start),
        endDate: format(end)
      });
      break;
    }

    case "last3Months": {
      const start = new Date(today.getFullYear(), today.getMonth() - 2, 1);
      const end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      setDateRange({
        startDate: format(start),
        endDate: format(end)
      });
      break;
    }

    default:
      break;
  }
  };

  // Validate date range
  const validateDateRange = () => {
    const today = new Date().toISOString().split('T')[0];
    const startDate = new Date(dateRange.startDate);
    const endDate = new Date(dateRange.endDate);
    const todayDate = new Date(today);

    // Check if start date is in the future
    if (startDate > todayDate) {
      setError('Start date cannot be in the future');
      return false;
    }

    // Check if end date is in the future
    if (endDate > todayDate) {
      setError('End date cannot be in the future');
      return false;
    }

    // Check if start date is after end date
    if (startDate > endDate) {
      setError('Start date cannot be after end date');
      return false;
    }

    return true;
  };

  // Handle export submission
  const handleExport = async () => {
    if (!validateDateRange()) {
      return;
    }

    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const payload = {
        start_date: dateRange.startDate,
        end_date: dateRange.endDate
      };

      console.log('Field Visit Export payload:', payload);

      const endpoint = `${API_ENDPOINTS.GET_export_excel_field_visit}?start_date=${payload.start_date}&end_date=${payload.end_date}`;
      const filename = `field_visit_export_${payload.start_date}_to_${payload.end_date}.xlsx`;
      
      await apiService.downloadFile(endpoint, filename);

      setSuccess('Field Visit data exported successfully!');
      setTimeout(() => {
        handleClose();
        onRefresh?.();
      }, 2000);
    } catch (error) {
      console.error('Error exporting field visit data:', error);
      setError(error.message || 'Failed to export field visit data. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      // Reset form
      setError('');
      setSuccess('');
      setDateFilter('thisMonth');
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ExportIcon color="primary" />
            <Typography variant="h6" fontWeight={600}>
              Export Field Visit Data
            </Typography>
          </Box>
          <IconButton onClick={handleClose} disabled={submitting}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Select date range to export field visit data to Excel
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pb: 1 }}>
        <Box sx={{ mt: 1 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }} icon={<CheckIcon />}>
              {success}
            </Alert>
          )}

          <Grid container spacing={3}>
            {/* Date Range Filter */}
            <Grid item xs={12}>
              <StyledCard>
                <CardContent>
                  <SectionHeader>
                    <DateIcon />
                    <Typography variant="h6">Date Range</Typography>
                  </SectionHeader>
                  
                  <FormControl component="fieldset" sx={{ mb: 2 }}>
                    <RadioGroup
                      row
                      value={dateFilter}
                      onChange={(e) => handleDateFilterChange(e.target.value)}
                      sx={{ gap: 2 }}
                    >
                      <FormControlLabel value="custom" control={<Radio />} label="Custom Range" />
                      <FormControlLabel value="thisMonth" control={<Radio />} label="This Month" />
                      <FormControlLabel value="lastMonth" control={<Radio />} label="Last Month" />
                      <FormControlLabel value="last3Months" control={<Radio />} label="Last 3 Months" />
                    </RadioGroup>
                  </FormControl>

                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Start Date"
                        type="date"
                        value={dateRange.startDate}
                        onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                        InputLabelProps={{ shrink: true }}
                        disabled={dateFilter !== 'custom'}
                        inputProps={{ 
                          max: new Date().toISOString().split('T')[0] // Prevent future dates
                        }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="End Date"
                        type="date"
                        value={dateRange.endDate}
                        onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                        InputLabelProps={{ shrink: true }}
                        disabled={dateFilter !== 'custom'}
                        inputProps={{ 
                          max: new Date().toISOString().split('T')[0], // Prevent future dates
                          min: dateRange.startDate 
                        }}
                      />
                    </Grid>
                  </Grid>

                  <Alert severity="info" sx={{ mt: 2 }}>
                    <Typography variant="body2">
                      <strong>Note:</strong> Only current date and past dates are allowed. Future dates are not permitted.
                    </Typography>
                  </Alert>
                </CardContent>
              </StyledCard>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button 
          onClick={handleClose} 
          disabled={submitting}
          sx={{ textTransform: 'none' }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleExport}
          variant="contained"
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={16} /> : <DownloadIcon />}
          sx={{ textTransform: 'none', minWidth: 120 }}
        >
          {submitting ? 'Exporting...' : 'Export to Excel'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FieldVisitExportModal;
